import React, { useRef, useEffect, useState, forwardRef } from "react";
import "isomorphic-fetch";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  updateTextImage,
  repositionAnchoredElement,
  firstTextInitialPosition,
  newTextInitialPosition
} from "../../../../../../actions";
import "./TextElement.css";
import ItemTypes from "../../../../../../constants/ItemTypes";
import Text from "./Text";

const calculatePosition = (props) => {
  let position = {};
  if (props.noOffset) {
    return position;
  }
  const scale = 72 * props.design.preview_scale;

  // Ensure we have valid numbers for positions
  const topPosition = props.textElement.top_position || 0;
  const leftPosition = props.textElement.left_position || 0;

  position.top = topPosition * scale;
  position.left = leftPosition * scale;

  if (props.offsetX) {
    position.left -= props.offsetX;
  }
  if (props.offsetY) {
    position.top -= props.offsetY;
  }

  // Ensure we don't return NaN values
  if (isNaN(position.top)) position.top = 0;
  if (isNaN(position.left)) position.left = 0;

  return position;
};

const TextElement = forwardRef((props, ref) => {
  const textElementRef = useRef();
  const [imageData, setImageData] = useState(props.url || null);
  const prevPropsRef = useRef();

  const fetchImage = () => {
    let fallBackImage = '';
    if (!props.imageUrl){
      fallBackImage = setImageUrl();
    }

    fetch(props.imageUrl ? props.imageUrl : fallBackImage).then((response) => {
      return response.json().then((json) => {
        setImageData(json.image);
        if (textElementRef.current) {
          const scale = props.previewScale * 72,
            newWidth = json.width / scale,
            newHeight = json.height / scale,
            oldWidth = props.textElement.width,
            oldHeight = props.textElement.height;

          if (props.textElement.first_text_field) {
            props.firstTextInitialPosition({
              layerId: props.layerId,
              index: 0,
              width: newWidth
            });
          }

          if (props.textElement.new_text) {
            props.newTextInitialPosition({
              layerId: props.layerId,
              index: props.index,
              width: newWidth
            });
          }

          props.updateTextImage({
            layerId: props.layerId,
            index: props.index,
            image: json.image,
            width: json.width,
            height: json.height,
            currentLayerId: props.layerId
          });

          if (
            props.textElement.anchor_point &&
            (oldWidth !== newWidth || oldHeight !== newHeight)
          ) {
            props.repositionAnchoredElement({
              oldWidth,
              newWidth,
              oldHeight,
              newHeight,
              index: props.index,
              layerId: props.layerId,
              itemType: props.itemType
            });
          }
        }
      });
    });
  };

  /* This is doing the same thing as "setImageUrl" in "DraggableTextElement"
    Dragging creates a new TextElement that doesn't have the text url assigned to it
    This gives the dragged element the correct url to render
  */
  const setImageUrl = () => {
    const font = props.textElement.font || "fixed",
      size = props.textElement.size,
      urlEncodeText = encodeURIComponent(props.textElement.text),
      transform_type = props.textElement.transform_type,
      transform_amount = props.textElement.transform_amount,
      letter_spacing = props.textElement.letter_spacing,
      rotation = props.textElement.rotation,
      layer = props.design.layer_set[props.layerId],
      embossed = layer.is_embossed ? 1 : 0,
      repousse = layer.is_repousse ? 1 : 0,
      cutout = layer.is_cutout ? 1 : 0,
      shadowcut = layer.is_shadow_cut ? 1 : 0,
      raised = layer.is_raised ? 1 : 0;

    let color = layer.print_color;
    if (layer.print_color_from_layer !== "") {
      let fromLayer = props.design.layer_set[layer.print_color_from_layer];
      if (layer.print_color_type === "Paper") {
        color = fromLayer.color;
      } else {
        color = fromLayer.print_color;
      }
    }

    const color_id = color ? color.id : "24";
    let style = `&embossed=${embossed}&repousse=${repousse}&cutout=${cutout}&shadowcut=${shadowcut}&raised=${raised}`;
    let path = `text?text=${urlEncodeText}&font_id=${font.id}&font_size=${size}&color_id=${color_id}&format=png&letter_spacing=${letter_spacing}&rotation=${rotation}&dimensions=1${style}`;
    let url = process.env.REACT_APP_TEXT_EFFECTS_URL + path;
    url += "&scale=" + props.design.preview_scale;
    if (transform_amount && transform_type) {
      url += `&text_effect=${transform_type}&transform_amount=${transform_amount}`;
    }

    // console.log(url);
    return url;
  };

  useEffect(() => {
    if (!imageData && !props.noFetch) {
      fetchImage();
    }
  }, [imageData]);

  useEffect(() => {
    if (prevPropsRef.current && !props.noFetch) {
      const prevProps = prevPropsRef.current;
      if (
        prevProps.imageUrl !== props.imageUrl ||
        props.layerId !== props.textElement.current_layer_id
      ) {
        fetchImage();
      }
    }
    prevPropsRef.current = props;
  }, [props.imageUrl, props.layerId, props.textElement.current_layer_id]);

  return (
    <div className="text-element" style={calculatePosition(props)} ref={ref}>
      <Text
        index={props.index}
        layerId={props.layerId}
        textElement={props.textElement}
        url={imageData || props.imageUrl}
        itemType={ItemTypes.TEXT}
        disableInteractivity={props.disableInteractivity}
        onShow2ndTip={props.onShow2ndTip}
        didShow2ndTip={props.didShow2ndTip}
        handleHide2ndTip={props.handleHide2ndTip}
        handleSelectSingleElement={props.handleSelectSingleElement}
        handleSelectMultipleElements={props.handleSelectMultipleElements}
        ref={textElementRef}
      />
    </div>
  );
});

TextElement.displayName = "TextElement";

const mapStateToProps = (state) => ({
  previewScale: state.design.present.preview_scale
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateTextImage,
      repositionAnchoredElement,
      firstTextInitialPosition,
      newTextInitialPosition
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TextElement);
